import React, { useRef } from 'react';
import { useReactToPrint } from 'react-to-print';
import { Button } from 'antd';
import dayjs from 'dayjs';
import { numberToWords } from 'amount-to-words';
import fmt from 'indian-number-format';

export interface DepositReceipt {
  depositNumber: string;
  amount: number;
  onlineAmount?: number;
  offlineAmount?: number;
  interest: number;
  investedDate: string;
  nominee: string;
  modeOfPayment: string;
  branch: {
    name: string;
    address: string;
    phoneNumber1: string;
    phoneNumber2: string;
    code: string;
  };
  customer: {
    customerId: string;
    name: string;
    fatherName: string;
    address: string;
    phoneNumber: string;
    aadhar: string;
    photo: string;
  };
}

interface Props {
  receipt: DepositReceipt;
}

const DepositReceiptPrintable: React.FC<Props> = ({ receipt }) => {
  const componentRef = useRef<HTMLDivElement>(null);

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });

  const orgName = localStorage.getItem('organizationName') ?? '';
  const orgLogo = localStorage.getItem('organizationLogo') ?? undefined;

  const cellStyle: React.CSSProperties = {
    border: '1px solid black',
    padding: '8px 12px',
    fontSize: 14,
  };

  const labelStyle: React.CSSProperties = {
    ...cellStyle,
    fontWeight: 600,
    backgroundColor: '#f5f5f5',
    width: '40%',
  };

  const valueStyle: React.CSSProperties = {
    ...cellStyle,
    width: '60%',
  };

  return (
    <div>
      <Button
        onClick={() => {
          handlePrint('');
        }}
        className="print__button"
        style={{ marginBottom: 16 }}
      >
        <span>Print Receipt</span>
      </Button>
      <div ref={componentRef} style={{ padding: 20 }}>
        <div
          style={{
            border: '2px solid black',
            padding: 20,
            maxWidth: 700,
            margin: '0 auto',
          }}
        >
          {/* Header with Logo */}
          <div style={{ textAlign: 'center', marginBottom: 20 }}>
            {orgLogo !== undefined && orgLogo !== '' && (
              <img
                src={orgLogo}
                alt="Logo"
                style={{
                  width: 80,
                  height: 80,
                  objectFit: 'contain',
                  marginBottom: 8,
                }}
              />
            )}
            <h2 style={{ margin: 0, fontSize: 24, fontWeight: 'bold' }}>
              ADVANCE RECEIPT
            </h2>
          </div>

          {/* Company and Customer Photo Section */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              marginBottom: 20,
            }}
          >
            <div style={{ flex: 1 }}>
              <p style={{ margin: '4px 0', fontWeight: 600 }}>
                Company Name: {orgName}
              </p>
              <p style={{ margin: '4px 0' }}>Branch: {receipt.branch.name}</p>
              <p style={{ margin: '4px 0' }}>
                Address: {receipt.branch.address}
              </p>
            </div>
            <div
              style={{
                width: 120,
                height: 140,
                border: '1px solid #ccc',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
              }}
            >
              {receipt.customer.photo !== '' ? (
                <img
                  src={receipt.customer.photo}
                  alt="Customer"
                  style={{ width: 110, height: 130, objectFit: 'cover' }}
                />
              ) : (
                <span style={{ fontSize: 10, textAlign: 'center', padding: 8 }}>
                  Customer Photo
                </span>
              )}
            </div>
          </div>

          {/* Receipt Number and Date */}
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              marginBottom: 20,
            }}
          >
            <tbody>
              <tr>
                <td style={labelStyle}>Advance Receipt No</td>
                <td style={valueStyle}>{receipt.depositNumber}</td>
              </tr>
              <tr>
                <td style={labelStyle}>Date</td>
                <td style={valueStyle}>
                  {dayjs(receipt.investedDate).format('DD / MM / YYYY')}
                </td>
              </tr>
            </tbody>
          </table>

          {/* Customer Details Section */}
          <div style={{ marginBottom: 20 }}>
            <h4
              style={{
                margin: 0,
                backgroundColor: '#e0e0e0',
                padding: '8px 12px',
                border: '1px solid black',
                borderBottom: 'none',
              }}
            >
              Customer Details
            </h4>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                <tr>
                  <td style={labelStyle}>Customer Name</td>
                  <td style={valueStyle}>{receipt.customer.name}</td>
                </tr>
                <tr>
                  <td style={labelStyle}>Father / Spouse Name</td>
                  <td style={valueStyle}>{receipt.customer.fatherName}</td>
                </tr>
                <tr>
                  <td style={labelStyle}>Address</td>
                  <td style={valueStyle}>{receipt.customer.address}</td>
                </tr>
                <tr>
                  <td style={labelStyle}>Mobile Number</td>
                  <td style={valueStyle}>{receipt.customer.phoneNumber}</td>
                </tr>
                <tr>
                  <td style={labelStyle}>ID Proof (Aadhar / PAN)</td>
                  <td style={valueStyle}>{receipt.customer.aadhar}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Advance Details Section */}
          <div style={{ marginBottom: 20 }}>
            <h4
              style={{
                margin: 0,
                backgroundColor: '#e0e0e0',
                padding: '8px 12px',
                border: '1px solid black',
                borderBottom: 'none',
              }}
            >
              Advance Details
            </h4>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                <tr>
                  <td style={labelStyle}>Advance Amount</td>
                  <td style={valueStyle}>
                    &#8377; {fmt.format(receipt.amount)}
                  </td>
                </tr>
                <tr>
                  <td style={labelStyle}>Amount in Words</td>
                  <td style={valueStyle}>
                    {numberToWords(receipt.amount)} Rupees Only
                  </td>
                </tr>
                <tr>
                  <td style={labelStyle}>Interest Rate</td>
                  <td style={valueStyle}>{receipt.interest} % p.a</td>
                </tr>
                <tr>
                  <td style={labelStyle}>Date of Disbursement</td>
                  <td style={valueStyle}>
                    {dayjs(receipt.investedDate).format('DD / MM / YYYY')}
                  </td>
                </tr>
                <tr>
                  <td style={labelStyle}>Expiry Date</td>
                  <td style={valueStyle}>
                    {dayjs(receipt.investedDate).add(1, 'year').subtract(1, 'day').format('DD / MM / YYYY')}
                  </td>
                </tr>
                {/* <tr>
                  <td style={labelStyle}>Mode of Payment</td>
                  <td style={valueStyle}>{receipt.modeOfPayment}</td>
                </tr> */}
                <tr>
                  <td style={labelStyle}>Nominee Name</td>
                  <td style={valueStyle}>{receipt.nominee}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Signature Section */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginTop: 40,
              paddingTop: 20,
            }}
          >
            <div style={{ textAlign: 'center' }}>
              <div
                style={{
                  borderTop: '1px solid black',
                  width: 200,
                  paddingTop: 8,
                }}
              >
                Customer Signature
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div
                style={{
                  borderTop: '1px solid black',
                  width: 200,
                  paddingTop: 8,
                }}
              >
                Authorized Signatory
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DepositReceiptPrintable;
